import { useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, TextField, Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { Bill } from '../../types'
import { SaleReturn } from '../../services/saleReturns/saleReturnService'
import { useReverseSaleReturn } from '../../hooks/api/useSaleReturns'
import { formatCurrency } from '../../utils/formatters'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface Props {
  bill: Bill
  returns: SaleReturn[]
}

const SaleReturnHistory = ({ bill, returns }: Props) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const [selected, setSelected] = useState<SaleReturn | null>(null)
  const [reason, setReason] = useState('')
  const reverse = useReverseSaleReturn()
  const latestActive = returns.filter((entry) => !entry.is_reversed)
    .sort((a, b) => b._id.localeCompare(a._id))[0]?._id

  const close = () => { setSelected(null); setReason('') }
  const confirm = () => {
    if (!selected) return
    reverse.mutate({ id: selected._id, billId: bill._id, reason: reason.trim() }, { onSuccess: close })
  }

  return <>
    <Box display="flex" flexDirection="column" gap={1.5}>
      {returns.length === 0 && <Typography variant="body2" color="text.secondary">No sale returns recorded.</Typography>}
      {returns.map((entry) => <Paper variant="outlined" sx={{ p: 1.5 }} key={entry._id}>
        <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Box>
            <Typography fontWeight={600}>
              {format(new Date(entry.return_date), 'dd MMM yyyy')} · Rs {formatCurrency(entry.credit_amount)} return credit
            </Typography>
            {entry.reason && <Typography variant="body2" color="text.secondary">{entry.reason}</Typography>}
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            {entry.is_reversed ? <Chip size="small" label="Reversed" /> : <Chip size="small" color="success" label="Active" />}
            {!entry.is_reversed && entry._id === latestActive &&
              <Button size="small" color="warning" onClick={() => setSelected(entry)}>Reverse</Button>}
          </Box>
        </Box>
        {entry.items.map((item) => {
          const line = bill.sku_items.find((billItem) => billItem._id === item.bill_item_id)
          const name = line && typeof line.sku_id === 'object' ? line.sku_id.name : 'Invoice item'
          return <Typography key={item.bill_item_id} variant="body2" sx={{ mt: 0.5 }}>
            {name}: {item.quantity_ctn.toLocaleString('en-US', { maximumFractionDigits: 3 })} {uom.code}
            {' '}({item.quantity_pcs} pieces) · Rs {formatCurrency(item.credit_amount)}
            {item.stock_restored ? ' · Stock restored' : ''}
          </Typography>
        })}
        {entry.is_reversed && entry.reversal_reason && <Typography variant="caption" color="text.secondary">
          Reversal reason: {entry.reversal_reason}
        </Typography>}
      </Paper>)}
    </Box>
    <Dialog open={Boolean(selected)} onClose={reverse.isPending ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>Reverse Sale Return</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          This will reverse the return credit and its stock movement. Any linked cash payment will also be corrected.
        </Typography>
        <TextField fullWidth multiline minRows={2} label="Reason for reversal (optional)" value={reason}
          onChange={(event) => setReason(event.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={reverse.isPending}>Cancel</Button>
        <Button color="warning" variant="contained" onClick={confirm} disabled={reverse.isPending}>
          {reverse.isPending ? 'Reversing...' : 'Reverse Return'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
}

export default SaleReturnHistory
